import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { PhoneCallIcon, VideoIcon, MessageSquareIcon, UsersIcon, ScalingIcon, DollarSignIcon, ShieldCheckIcon, ArrowRightIcon, CloudIcon, MonitorSmartphoneIcon } from 'lucide-react';
const features = [{
  icon: ScalingIcon,
  title: 'Scalable to Your Needs',
  description: "Whether you're running a small business or managing a large enterprise, our UCaaS solution grows with you. Easily add or remove users as needed, giving you the flexibility to scale without limits.",
  color: 'text-blue-500',
  bg: 'bg-blue-50'
}, {
  icon: DollarSignIcon,
  title: 'Cost-Efficient Solution',
  description: 'Say goodbye to the high costs of traditional phone systems. Our cloud-based UCaaS solution offers predictable pricing and eliminates the need for expensive hardware, helping you save money while enhancing communication.',
  color: 'text-green-500',
  bg: 'bg-green-50'
}, {
  icon: ShieldCheckIcon,
  title: 'Unmatched Reliability',
  description: 'With 99.99% uptime, our UCaaS platform ensures your business stays connected without interruptions. Powered by cutting-edge technology, you can trust that your communications will always be reliable.',
  color: 'text-purple-500',
  bg: 'bg-purple-50'
}];
const tools = [{
  icon: PhoneCallIcon,
  label: 'Voice Calls',
  color: 'text-blue-500',
  bg: 'bg-blue-50'
}, {
  icon: VideoIcon,
  label: 'Video Meetings',
  color: 'text-red-500',
  bg: 'bg-red-50'
}, {
  icon: MessageSquareIcon,
  label: 'Instant Messaging',
  color: 'text-green-500',
  bg: 'bg-green-50'
}, {
  icon: UsersIcon,
  label: 'Collaboration',
  color: 'text-amber-500',
  bg: 'bg-amber-50'
}, {
  icon: CloudIcon,
  label: 'Cloud-Based',
  color: 'text-violet-500',
  bg: 'bg-violet-50'
}, {
  icon: MonitorSmartphoneIcon,
  label: 'Any Device',
  color: 'text-teal-500',
  bg: 'bg-teal-50'
}];
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.4
    }
  }
};
export function UCaaSPage({
  onNavigate
}: {
  onNavigate: (page: string) => void;
}) {
  return <div data-id="element-900">
      {/* Hero */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-24 overflow-hidden bg-slate-900" data-id="element-901">
        <div className="absolute inset-0 bg-grid-pattern opacity-20" data-id="element-902"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-slate-900/90" data-id="element-903"></div>
        <div className="absolute top-0 left-1/2 -translate-x-1/3 -translate-y-1/4 w-[700px] h-[700px] bg-indigo-500/15 rounded-full blur-[120px] pointer-events-none" data-id="element-904"></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-905">
          <div className="max-w-3xl" data-id="element-906">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6
          }} data-id="element-907">
              <div className="inline-flex items-center px-4 py-2 rounded-full bg-white/10 border border-white/20 text-white text-sm font-medium mb-6 backdrop-blur-sm" data-id="element-908">
                <span className="flex h-2 w-2 rounded-full bg-indigo-400 mr-2" data-id="element-909"></span>
                Cloud-Based Communications
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white leading-tight mb-4 tracking-tight" data-id="element-910">
                Unified Communications
                <br className="hidden md:block" data-id="element-911" />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-blue-400" data-id="element-912">
                  as a Service (UCaaS)
                </span>
              </h1>
              <p className="text-2xl md:text-3xl text-slate-300 font-medium mb-6" data-id="element-913">
                Stay connected, Anywhere, Anytime
              </p>
              <p className="text-lg text-slate-400 leading-relaxed mb-8 max-w-2xl" data-id="element-914">
                All your essential communication tools in one easy-to-use
                platform — voice calls, video meetings, instant messaging, and
                collaboration tools accessible on any device.
              </p>
              <button onClick={() => onNavigate('home')} className="bg-primary hover:bg-primary-dark text-white px-8 py-4 rounded-full font-bold text-lg transition-colors shadow-lg inline-flex items-center" data-id="element-915">
                Get Started with UCaaS
                <ArrowRightIcon className="w-5 h-5 ml-2" data-id="element-916" />
              </button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* What is UCaaS */}
      <section className="py-20 bg-white" data-id="element-917">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-918">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-919">
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} data-id="element-920">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6" data-id="element-921">
                What is UCaaS?
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed mb-8" data-id="element-922">
                Unified Communications as a Service (UCaaS) is a cloud-based
                solution that brings all your essential communication tools into
                one easy-to-use platform. Imagine having voice calls, video
                meetings, instant messaging, and collaboration tools all in one
                place—accessible anytime, anywhere, on any device. That's the
                power of UCaaS.
              </p>
            </motion.div>

            <motion.div className="grid grid-cols-2 sm:grid-cols-3 gap-4" variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} data-id="element-923">
              {tools.map((tool, i) => {
              const Icon = tool.icon;
              return <motion.div key={i} variants={itemVariants} className="bg-slate-50 rounded-2xl p-5 border border-slate-100 text-center" data-id="element-924">
                    <div className={`w-12 h-12 ${tool.bg} rounded-xl flex items-center justify-center mx-auto mb-3`} data-id="element-925">
                      <Icon className={`w-6 h-6 ${tool.color}`} data-id="element-926" />
                    </div>
                    <span className="text-sm font-semibold text-slate-800" data-id="element-927">
                      {tool.label}
                    </span>
                  </motion.div>;
            })}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Key Benefits */}
      <section className="py-20 bg-slate-50" data-id="element-928">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-929">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-930">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-id="element-931">
              Why Choose UCaaS?
            </h2>
            <p className="text-lg text-slate-600" data-id="element-932">
              A smarter, more affordable way to keep your entire team connected
              and productive.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-933">
            {features.map((feature, i) => {
            const Icon = feature.icon;
            return <motion.div key={i} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: i * 0.1
            }} className="bg-white rounded-2xl p-8 border border-slate-100 shadow-sm flex flex-col" data-id="element-934">
                  <div className={`w-14 h-14 ${feature.bg} rounded-2xl flex items-center justify-center mb-6`} data-id="element-935">
                    <Icon className={`w-7 h-7 ${feature.color}`} data-id="element-936" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3" data-id="element-937">
                    {feature.title}
                  </h3>
                  <p className="text-slate-600 leading-relaxed flex-grow" data-id="element-938">
                    {feature.description}
                  </p>
                </motion.div>;
          })}
          </div>
        </div>
      </section>

      {/* Uptime Highlight */}
      <section className="py-16 bg-white" data-id="element-939">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-940">
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="bg-slate-900 rounded-3xl p-10 md:p-16 text-center relative overflow-hidden" data-id="element-941">
            <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/20 rounded-full blur-[80px] pointer-events-none" data-id="element-942"></div>
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-blue-500/15 rounded-full blur-[60px] pointer-events-none" data-id="element-943"></div>
            <div className="relative z-10" data-id="element-944">
              <div className="text-6xl md:text-7xl font-extrabold text-white mb-4" data-id="element-945">
                99.99%
              </div>
              <div className="text-xl text-slate-300 font-medium mb-2" data-id="element-946">
                Uptime Guarantee
              </div>
              <p className="text-slate-400 max-w-lg mx-auto" data-id="element-947">
                Your business stays connected without interruptions, powered by
                cutting-edge cloud technology you can always rely on.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Experience the Future */}
      <section className="bg-primary relative overflow-hidden py-20" data-id="element-948">
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none" data-id="element-949">
          <div className="absolute -top-24 -right-24 w-96 h-96 bg-white opacity-5 rounded-full blur-3xl" data-id="element-950"></div>
          <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-white opacity-10 rounded-full blur-3xl" data-id="element-951"></div>
        </div>

        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center z-10" data-id="element-952">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" data-id="element-953">
            Experience the Future of Business Communication
          </h2>
          <p className="text-xl text-blue-100 mb-10 max-w-3xl mx-auto leading-relaxed" data-id="element-954">
            Transform your business communications with UCaaS and experience
            unparalleled efficiency and connectivity. Our UCaaS solution
            integrates all your communication needs into a single, scalable
            platform, enhancing collaboration, reducing costs, and ensuring
            top-notch security. Don't let outdated systems hold you back—embrace
            the future of communication today.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-955">
            <button onClick={() => onNavigate('home')} className="w-full sm:w-auto bg-white text-primary hover:bg-slate-50 px-8 py-4 rounded-full font-bold text-lg transition-colors shadow-lg inline-flex items-center justify-center" data-id="element-956">
              Contact Us Today
              <ArrowRightIcon className="w-5 h-5 ml-2" data-id="element-957" />
            </button>
            <a href="tel:+4105255284" className="w-full sm:w-auto bg-transparent border-2 border-white text-white hover:bg-white/10 px-8 py-4 rounded-full font-bold text-lg transition-colors" data-id="element-958">
              Call Us: +410-525-5284
            </a>
          </div>
        </div>
      </section>
    </div>;
}